import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { HintAnimation } from '../portal-hints/hint-motion';
import { FilmRef, filmFor } from './films/lesson-films';

/**
 * Film der Academy aus Szenen (`films/lesson-films.ts`): Hinweis-Film und/oder
 * Academy-Szenen. Mit `loop` als kurze Vorschau in Endlosschleife (wie die
 * Hinweis-Box), sonst einmal hintereinander mit Szenenmarken zum Springen,
 * Pause/Weiter, Neustart. Untertitel je Szene aus der Inhaltsdatei.
 *
 * `prefers-reduced-motion`: kein Abspielen, je Szene das Standbild
 * (`stillAt`) — die Marken wechseln die Szene. Verborgener Tab: steht.
 * Die Schleife läuft außerhalb der Zone und setzt nur Styles; Angular
 * erfährt nur Szenenwechsel und Ende.
 */
@Component({
  selector: 'app-academy-film',
  templateUrl: './academy-film.component.html',
  styleUrls: ['./academy-film.component.scss'],
})
export class AcademyFilmComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input({ required: true }) scenes: FilmRef[] = [];
  @Input() captions: string[] = [];
  @Input() label = '';
  /** Vorschau: die Szene(n) ohne Ende wiederholen (eine Szene = Portalhinweis-Film). */
  @Input() loop = false;

  @ViewChild('stage', { static: true }) stageRef!: ElementRef<HTMLElement>;

  index = 0;
  playing = false;
  ended = false;
  width = 0;
  /** Dauer je Szene in Sekunden (für Marken und Gesamtbalken). */
  durations: number[] = [];
  readonly reducedMotion =
    typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

  private films: HintAnimation[] = [];
  private t = 0;
  private last = 0;
  private raf = 0;
  private viewReady = false;
  private observer?: ResizeObserver;
  private readonly onVisibility = () => {
    if (document.hidden) this.pause();
  };

  constructor(
    private host: ElementRef<HTMLElement>,
    private zone: NgZone,
  ) {}

  get scale(): number {
    return this.width / 640;
  }

  get height(): number {
    return Math.round(354 * this.scale);
  }

  get total(): number {
    return this.durations.reduce((a, b) => a + b, 0);
  }

  get caption(): string {
    return this.captions[this.index] ?? '';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['scenes']) {
      this.films = this.scenes.map((ref) => filmFor(ref));
      this.durations = this.films.map((f) => f.playbackSeconds);
      if (this.viewReady) this.restart(false);
    }
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.measure();
    if (typeof ResizeObserver === 'function') {
      this.observer = new ResizeObserver(() => this.zone.run(() => this.measure()));
      this.observer.observe(this.host.nativeElement);
    }
    document.addEventListener('visibilitychange', this.onVisibility);
    // Einbauen nach dem ersten Durchlauf der Change Detection.
    queueMicrotask(() => this.zone.run(() => this.restart(!this.reducedMotion)));
  }

  ngOnDestroy(): void {
    cancelAnimationFrame(this.raf);
    this.observer?.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  toggle(): void {
    if (this.ended) this.restart(true);
    else if (this.playing) this.pause();
    else this.play();
  }

  restart(autoplay = true): void {
    this.ended = false;
    this.show(0);
    if (autoplay && !this.reducedMotion) this.play();
  }

  /** Marke angeklickt: Szene von vorn (bei reduced motion: ihr Standbild). */
  jump(i: number): void {
    const wasPlaying = this.playing || this.ended;
    this.pause();
    this.ended = false;
    this.show(i);
    if (wasPlaying && !this.reducedMotion) this.play();
  }

  /** Anteil der Szene i, der schon gelaufen ist (0…1) — für die Marken. */
  progressOf(i: number): number {
    if (i < this.index) return 1;
    if (i > this.index) return 0;
    return this.durations[i] ? Math.min(1, this.t / this.durations[i]) : 0;
  }

  trackScene(i: number): number {
    return i;
  }

  private play(): void {
    if (this.playing || !this.films.length) return;
    this.playing = true;
    this.last = performance.now();
    this.zone.runOutsideAngular(() => {
      const tick = (now: number) => {
        this.t += (now - this.last) / 1000;
        this.last = now;
        const film = this.films[this.index];
        if (this.t >= film.playbackSeconds) {
          if (this.index + 1 < this.films.length) {
            this.zone.run(() => this.show(this.index + 1));
          } else if (this.loop) {
            this.zone.run(() => this.show(0));
          } else {
            film.render(film.playbackSeconds - 0.001);
            this.zone.run(() => {
              this.playing = false;
              this.ended = true;
            });
            this.paintProgress();
            return;
          }
        } else {
          film.render(this.t);
        }
        this.paintProgress();
        this.raf = requestAnimationFrame(tick);
      };
      this.raf = requestAnimationFrame(tick);
    });
  }

  private pause(): void {
    cancelAnimationFrame(this.raf);
    this.playing = false;
  }

  private show(i: number): void {
    const film = this.films[i];
    if (!film) return;
    this.index = i;
    this.t = this.reducedMotion ? film.stillAt : 0;
    const stage = this.stageRef.nativeElement;
    stage.style.width = `${film.width}px`;
    stage.style.height = `${film.height}px`;
    film.mount(stage);
    film.render(this.t);
    this.paintProgress();
  }

  /** Die Füllung der aktuellen Marke direkt setzen — ohne Change Detection je Bild. */
  private paintProgress(): void {
    const marks = this.host.nativeElement.querySelectorAll<HTMLElement>('.academy-film__mark-fill');
    marks.forEach((el, i) => (el.style.transform = `scaleX(${this.progressOf(i)})`));
  }

  private measure(): void {
    const w = Math.floor(this.host.nativeElement.clientWidth);
    if (w > 0 && w !== this.width) this.width = w;
  }
}
